// WorkAbroadPricing.js
// PRICES NOT SUPPLIED BY IVIPASS: the $199 per interview slot figure below is
// inherited from the cloned site and has been left untouched on purpose. It is a
// placeholder and must be confirmed or replaced by Ivipass before launch.
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const WorkAbroadPricing = () => {
  const coverageItems = [
    'Checking your eligibility and matching you to suitable roles',
    'Adding you to the work abroad pipeline',
    'Arranging interviews with the employer involved',
    'Getting you ready for those interviews',
    'Guidance on documents and compliance requirements',
    'Continued help while the hiring runs its course'
  ];

  const importantNotes = [
    'What you pay covers the support work and nothing else',
    'No fee depends on a salary figure or on being hired',
    'Any contract of employment is between you and the employer',
    'Ivipass never receives wages or commission from employers'
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              What Work Abroad Support Costs
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6"
          >
            <h3 className="text-xl font-semibold text-primary mb-4">
              Work Abroad Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              These fees apply only once a candidate has qualified and entered the work abroad pipeline.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                A work abroad fee usually includes:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-surface p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-primary mb-4">
                Cost Per Interview
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-primary">One Interview Slot</p>
                    <p className="text-sm text-gray-600">Preparation and coordination, start to finish</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">$199</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">What this buys:</span> preparation beforehand, coordination with the employer around the interview, and continued guidance while the hiring decision is being made.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              Every charge is set out plainly before anyone joins the pipeline.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-primary">
                Worth knowing:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WorkAbroadPricing;
